<template>
    <div class="container">
        <h1 class="my-3">{{ $t('nav.projects') }}</h1>

        <div class="input-group mb-3 rounded-pill">
            <input type="text" class="form-control" :placeholder="$t('browse.searchProjects')" v-model="searchTerm"
                @keyup.enter.prevent="onSearchSubmit" />
            <span class="input-group-text bg-body" type="button" @click="onSearchSubmit">
                <i class="fa-solid fa-magnifying-glass"></i>
            </span>
        </div>

        <FilterGroups :filterGroups="filterGroups" @filters-changed="onFiltersChanged" />

        <div v-if="projects.length > 0">
            <ul class="list-group mb-3">
                <li v-for="project in projects" :key="project.id" class="list-group-item p-3">
                    <router-link :to="`/projects/${project.id}`" class="text-decoration-none">
                        <div class="row align-items-center">
                            <div class="col">
                                <div class="fw-bolder small">{{ project.name }}</div>
                                <div class="small">{{ project.address }}</div>
                            </div>
                            <div class="col-auto text-end">
                                <button class="btn-icon"><i class="fa-solid fa-arrow-right"></i></button>
                            </div>
                        </div>
                    </router-link>
                </li>
            </ul>

            <ListPagination :currentPage="currentPage" :totalTasks="totalItems" :limit="limit"
                @page-changed="onPageChanged" />
        </div>
        <div v-else></div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import ListPagination from '@/components/ListPagination.vue';
import FilterGroups from '@/components/FilterGroups.vue';

export default {
    components: { ListPagination, FilterGroups },

    data() {
        return {
            projects: [],
            currentPage: 1,   // mirrors ?page=
            totalItems: 0,
            limit: 20,
            searchTerm: '',   // mirrors ?search=
            activeFilters: [],
            lastRouteKey: '',

            filterGroups: [
                {
                    name: '',
                    multi: true,
                    filters: [
                        {
                            key: 'myRegion',
                            labelKey: 'browse.myRegion',
                            active: false,
                            activeClass: 'text-bg-primary',
                            inactiveClass: 'bg-body-tertiary text-body-secondary',
                            params: { user_region: '1' }
                        },
                        {
                            key: 'myProfileGroup',
                            labelKey: 'browse.myProfileGroup',
                            active: false,
                            activeClass: 'text-bg-primary',
                            inactiveClass: 'bg-body-tertiary text-body-secondary',
                            params: { user_profile_group: '1' }
                        }
                    ]
                }
            ]
        };
    },

    mounted() {
        // load saved filters (no fetch here)
        this.loadSelectedFilters();
    },

    watch: {
        '$route.query': {
            immediate: true,
            deep: true,
            handler(newQ) {
                // normalize page
                const pRaw = newQ.page ?? '1';
                const p = parseInt(pRaw, 10);
                if (!Number.isFinite(p) || p < 1) {
                    this.$router.replace({ query: { ...newQ, page: '1' } });
                    return;
                }

                // normalize search
                const s = (newQ.search ?? '').toString();

                // keep input synced with URL
                if (this.searchTerm !== s) this.searchTerm = s;

                const key = `${p}|${s}`;
                if (key === this.lastRouteKey) return;
                this.lastRouteKey = key;

                this.currentPage = p;
                this.fetchProjects(p, s);
            }
        }
    },

    methods: {
        onSearchSubmit() {
            const term = (this.searchTerm || '').trim();
            const q = { ...this.$route.query, page: '1' };
            if (term) q.search = term; else delete q.search;
            this.$router.push({ query: q });
            // watcher will fetch
        },

        onFiltersChanged(activeFilters) {
            this.activeFilters = activeFilters;
            const q = { ...this.$route.query, page: '1' };
            this.$router.push({ query: q });
            this.saveSelectedFilters();
            this.fetchProjects(1, this.searchTerm);
        },

        onPageChanged(newPage) {
            const q = { ...this.$route.query, page: String(newPage) };
            if (q.page !== this.$route.query.page) this.$router.push({ query: q });
            // watcher will fetch
        },

        async fetchProjects(page = 1, searchTerm = '') {
            try {
                let url = `/projects?not_active=0&type_id=0&page=${page}&limit=${this.limit}&search=${encodeURIComponent(searchTerm)}`;
                
                this.loadSelectedFilters();
                // filters only affect request, not URL
                const mergedParams = this.mergeFilterParams(this.activeFilters);
                Object.keys(mergedParams).forEach((key) => {
                    url += `&${key}=${encodeURIComponent(mergedParams[key])}`;
                });

                const response = await axios.get(url);
                this.projects = response.data.data;
                this.totalItems = response.data.total;
                this.currentPage = page;
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        },

        mergeFilterParams(filters) {
            const paramMap = {};
            filters.forEach((f) => {
                Object.entries(f.params).forEach(([key, rawValue]) => {
                    const values = String(rawValue).split(',');
                    (paramMap[key] ||= []).push(...values);
                });
            });
            const merged = {};
            Object.keys(paramMap).forEach((key) => {
                merged[key] = [...new Set(paramMap[key])].join(',');
            });
            return merged;
        },

        saveSelectedFilters() {
            const activeKeys = this.activeFilters.map((f) => f.key);
            localStorage.setItem('ProjectListFilterKeys', JSON.stringify(activeKeys));
        },

        loadSelectedFilters() {
            const stored = localStorage.getItem('ProjectListFilterKeys');
            if (!stored) return;
            try {
                const keysArray = JSON.parse(stored);
                this.filterGroups.forEach((group) => {
                    group.filters.forEach((filter) => {
                        filter.active = keysArray.includes(filter.key);
                    });
                });
            } catch (err) {
                console.warn('Could not parse saved filter keys:', err);
            }
            this.collectActiveFilters();
        },

        collectActiveFilters() {
            const active = [];
            this.filterGroups.forEach((group) => {
                group.filters.forEach((filter) => {
                    if (filter.active) active.push(filter);
                });
            });
            this.activeFilters = active;
        }
    }
};
</script>
